import React, {useEffect, useMemo, useRef, useState} from "react";
import {arrayMove, move} from "@dnd-kit/helpers";
import {DragDropProvider} from "@dnd-kit/react";
import {FaPlus} from "react-icons/fa6";
import Button from "../../../components/button";
import BoardHeader from "./board-header";
import BoardList from "./board-list";
import CardDetailsModal from "./card-details-modal";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const uniqueId = prefix => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const moveCardToList = (currentCards, cardId, targetListId) => {
    const entries = Object.entries(currentCards);
    const sourceEntry = entries.find(([, listCards]) => listCards.some(card => String(card.id) === String(cardId)));
    const targetEntry = entries.find(([listId]) => String(listId) === String(targetListId));
    if (!sourceEntry || !targetEntry || sourceEntry[0] === targetEntry[0]) return currentCards;

    const [sourceListId, sourceCards] = sourceEntry;
    const [targetListKey, targetCards] = targetEntry;
    const cardIndex = sourceCards.findIndex(card => String(card.id) === String(cardId));
    const movingCard = sourceCards[cardIndex];

    return {
        ...currentCards,
        [sourceListId]: sourceCards.filter((_, index) => index !== cardIndex),
        [targetListKey]: [...targetCards, movingCard],
    };
};

const normalizeBoardLabels = (labels, cards) => {
    const source = [
        ...(labels ?? []),
        ...Object.values(cards ?? {}).flatMap(listCards => listCards.flatMap(card => card.labels ?? [])),
    ];
    const seen = new Set();

    return source.reduce((result, label, index) => {
        const key = label.id ? `id:${label.id}` : `value:${label.text ?? ""}:${label.color ?? ""}`;
        if (seen.has(key)) return result;
        seen.add(key);
        result.push({...label, id: label.id ?? `legacy-label-${index}`});
        return result;
    }, []);
};

const normalizeCard = (card, boardLabels = []) => {
    const labels = (card.labels ?? []).map((label, index) => boardLabels.find(boardLabel =>
        (label.id && String(boardLabel.id) === String(label.id))
        || (!label.id && boardLabel.text === label.text && boardLabel.color === label.color),
    ) ?? {...label, id: label.id ?? `legacy-card-label-${index}`});
    return {
        clientId: String(card.clientId ?? card.id),
        description: "",
        labels,
        assignees: [],
        dueDate: "",
        dueDateValue: "",
        startDateValue: "",
        dueTime: "",
        recurrence: "never",
        reminder: "none",
        reminderEnabled: false,
        checklist: [],
        attachments: [],
        history: [],
        completed: false,
        ...card,
        comments: Array.isArray(card.comments) ? card.comments : [],
    };
};

const DemandBoard = ({board, initialLists, initialCards, users = [], currentUser, onBack, onSaveStructure, onSaveCardDetails, onCreateComment, onUpdateComment, onDeleteComment, onSaveLabels, isSaving, isEditing, editingUser, onStartEditing, onStopEditing, isChangingEditing}) => {
    const initialBoardLabels = useMemo(() => normalizeBoardLabels(board.labels, initialCards), [board.labels, initialCards]);
    const [boardLabels, setBoardLabels] = useState(initialBoardLabels);
    const [lists, setLists] = useState(() => initialLists.map(list => ({pinned: false, ...list, clientId: String(list.clientId ?? list.id)})));
    const [cards, setCards] = useState(() => Object.fromEntries(initialLists.map(list => [
        list.id,
        (initialCards[list.id] ?? []).map(card => normalizeCard(card, initialBoardLabels)),
    ])));
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [addCardRequests, setAddCardRequests] = useState({});
    const cardsBeforeDrag = useRef(cards);
    const listsBeforeDrag = useRef(lists);
    const cardsRef = useRef(cards);
    const listsRef = useRef(lists);
    const labelsRef = useRef(boardLabels);
    const structuralSaveChain = useRef(Promise.resolve());
    const labelsSaveChain = useRef(Promise.resolve());
    const detailsSaveChain = useRef(Promise.resolve());

    const commitCards = updater => {
        const next = typeof updater === "function" ? updater(cardsRef.current) : updater;
        cardsRef.current = next;
        setCards(next);
        return next;
    };
    const commitLists = updater => {
        const next = typeof updater === "function" ? updater(listsRef.current) : updater;
        listsRef.current = next;
        setLists(next);
        return next;
    };
    const commitLabels = updater => {
        const next = typeof updater === "function" ? updater(labelsRef.current) : updater;
        labelsRef.current = next;
        setBoardLabels(next);
        return next;
    };
    const persistStructure = (nextLists = listsRef.current, nextCards = cardsRef.current, historyChange = null, detailedCardIds = []) => {
        const detailedIds = new Set(detailedCardIds.map(String));
        const content = {
            lists: nextLists.map(({id, clientId, title, pinned}) => ({id, clientId, title, pinned})),
            cards: Object.fromEntries(Object.entries(nextCards).map(([listId, listCards]) => [
                listId,
                listCards.map(card => {
                    const structuralCard = {
                        id: card.id,
                        clientId: card.clientId,
                        title: card.title,
                        completed: card.completed,
                        ...(historyChange && String(historyChange.cardId) === String(card.id) ? {historyEntry: historyChange.entry} : {}),
                    };
                    if (!detailedIds.has(String(card.id))) return structuralCard;

                    return {
                        ...structuralCard,
                        description: card.description,
                        labels: card.labels,
                        assignees: card.assignees,
                        startDateValue: card.startDateValue,
                        dueDateValue: card.dueDateValue,
                        dueTime: card.dueTime,
                        reminder: card.reminder,
                        reminderEnabled: card.reminderEnabled,
                        checklists: card.checklists,
                        comments: card.comments,
                        history: card.history,
                    };
                }),
            ])),
        };
        structuralSaveChain.current = structuralSaveChain.current
            .catch(() => undefined)
            .then(() => onSaveStructure(content))
            .catch(() => undefined);
        return structuralSaveChain.current;
    };
    const persistCardDetails = (card, changes) => {
        const allowedFields = new Set(["description", "labels", "assignees", "startDateValue", "dueDateValue", "dueTime", "reminder", "reminderEnabled", "checklists", "attachments", "historyEntry"]);
        const detailChanges = Object.fromEntries(Object.entries(changes).filter(([key]) => allowedFields.has(key)));
        if (Object.keys(detailChanges).length === 0) return;
        const reference = card.clientId ?? card.id;
        detailsSaveChain.current = detailsSaveChain.current
            .catch(() => undefined)
            .then(() => Promise.all([
                structuralSaveChain.current.catch(() => undefined),
                labelsSaveChain.current.catch(() => undefined),
            ]))
            .then(() => onSaveCardDetails(reference, detailChanges))
            .then(response => {
                const updatedCard = response?.data ?? response;
                if (!updatedCard?.id) return;
                commitCards(currentCards => Object.fromEntries(Object.entries(currentCards).map(([listId, listCards]) => [
                    listId,
                    listCards.map(currentCard => String(currentCard.id) === String(card.id) ? {
                        ...currentCard,
                        attachments: updatedCard.attachments ?? currentCard.attachments ?? [],
                        history: updatedCard.history ?? currentCard.history ?? [],
                    } : currentCard),
                ])));
            })
            .catch(() => undefined);
    };
    const persistLabels = nextLabels => {
        labelsSaveChain.current = labelsSaveChain.current
            .catch(() => undefined)
            .then(() => onSaveLabels(nextLabels))
            .catch(() => undefined);
    };

    useEffect(() => {
        if (isEditing) return;

        const nextLabels = normalizeBoardLabels(board.labels, initialCards);
        const nextLists = initialLists.map(list => ({pinned: false, ...list, clientId: String(list.clientId ?? list.id)}));
        const nextCards = Object.fromEntries(initialLists.map(list => [
            list.id,
            (initialCards[list.id] ?? []).map(card => normalizeCard(card, nextLabels)),
        ]));
        labelsRef.current = nextLabels;
        listsRef.current = nextLists;
        cardsRef.current = nextCards;
        setBoardLabels(nextLabels);
        setLists(nextLists);
        setCards(nextCards);
    }, [board.labels, initialCards, initialLists, isEditing]);

    useEffect(() => {
        if (!isEditing) return;

        const remoteCards = Object.values(initialCards).flat();
        commitCards(currentCards => Object.fromEntries(Object.entries(currentCards).map(([listId, listCards]) => [
            listId,
            listCards.map(card => {
                const remoteCard = remoteCards.find(item => String(item.id) === String(card.id) || String(item.clientId) === String(card.clientId));
                return remoteCard ? {...card, comments: remoteCard.comments ?? [], history: remoteCard.history ?? []} : card;
            }),
        ])));
    }, [initialCards, isEditing]);

    const selectedCard = useMemo(() => Object.values(cards).flat().find(card => card.id === selectedCardId) ?? null, [cards, selectedCardId]);
    const selectedCardList = useMemo(() => lists.find(list => (cards[list.id] ?? []).some(card => card.id === selectedCardId)) ?? null, [cards, lists, selectedCardId]);

    const updateCard = (cardId, changes, historyText) => {
        const historyAuthor = currentUser?.name ?? currentUser?.login ?? "Usuário";
        let changedCard = null;
        let appliedPatch = {};
        let historyEntry = null;
        const nextCards = commitCards(currentCards => Object.fromEntries(Object.entries(currentCards).map(([listId, listCards]) => [
            listId,
            listCards.map(card => {
                if (card.id !== cardId) return card;
                const patch = typeof changes === "function" ? changes(card) : changes;
                appliedPatch = patch;
                historyEntry = historyText ? {
                    id: uniqueId("history"),
                    author: historyAuthor,
                    authorId: currentUser?.id ?? null,
                    text: historyText,
                    date: new Date().toLocaleString("pt-BR"),
                } : null;
                const history = historyEntry ? [...(card.history ?? []), historyEntry] : card.history;
                changedCard = {...card, ...patch, history};
                return changedCard;
            }),
        ])));
        if (!changedCard) return;
        const isStructuralChange = Object.keys(appliedPatch).every(key => ["title", "completed"].includes(key));
        if (isStructuralChange) persistStructure(listsRef.current, nextCards, historyEntry ? {cardId, entry: historyEntry} : null);
        else persistCardDetails(changedCard, {...appliedPatch, ...(historyEntry ? {historyEntry} : {})});
    };

    const replaceCardFromResponse = (cardId, response) => {
        const updatedCard = response?.data ?? response;
        if (!updatedCard?.id) return;

        commitCards(currentCards => Object.fromEntries(Object.entries(currentCards).map(([listId, listCards]) => [
            listId,
            listCards.map(card => String(card.id) === String(cardId) ? {
                ...card,
                comments: updatedCard.comments ?? [],
                history: updatedCard.history ?? [],
            } : card),
        ])));
    };

    const createCardComment = async (card, text) => {
        await structuralSaveChain.current.catch(() => undefined);
        const response = await onCreateComment(card.clientId ?? card.id, text);
        replaceCardFromResponse(card.id, response);
    };

    const updateCardComment = async (card, commentId, text) => {
        const response = await onUpdateComment(card.clientId ?? card.id, commentId, text);
        replaceCardFromResponse(card.id, response);
    };

    const deleteCardComment = async (card, commentId) => {
        const response = await onDeleteComment(card.clientId ?? card.id, commentId);
        replaceCardFromResponse(card.id, response);
    };

    const handleDragStart = () => {
        cardsBeforeDrag.current = cardsRef.current;
        listsBeforeDrag.current = listsRef.current;
    };

    const handleDragOver = event => {
        event.preventDefault();
        const {source, target} = event.operation;
        if (!source || !target) return;

        if (source.type === "demand-list") {
            const sourceId = source.data?.listId ?? String(source.id).replace("demand-list:", "");
            const targetId = target.data?.listId ?? String(target.id).replace("demand-list:", "");
            commitLists(currentLists => {
                const from = currentLists.findIndex(list => list.id === sourceId);
                let to = currentLists.findIndex(list => list.id === targetId);
                if (from < 0 || to < 0 || from === to) return currentLists;
                const movingList = currentLists[from];
                const pinnedCount = currentLists.filter(list => list.pinned).length;
                to = movingList.pinned ? Math.min(to, Math.max(0, pinnedCount - 1)) : Math.max(to, pinnedCount);
                return arrayMove(currentLists, from, to);
            });
            return;
        }

        if (source.type === "demand-card") {
            commitCards(currentCards => target.data?.isListDropzone
                ? moveCardToList(currentCards, source.id, target.data.listId)
                : move(currentCards, event));
        }
    };

    const handleDragEnd = event => {
        const {source, target, canceled} = event.operation;
        if (canceled) {
            commitCards(cardsBeforeDrag.current);
            commitLists(listsBeforeDrag.current);
            return;
        }
        if (source?.type === "demand-card" && target?.data?.isListDropzone) {
            commitCards(currentCards => moveCardToList(currentCards, source.id, target.data.listId));
        }
        if (source?.type === "demand-card" || source?.type === "demand-list") persistStructure();
    };

    const addCard = (listId, title) => {
        const newCard = normalizeCard({id: uniqueId("demand"), title});
        const nextCards = commitCards(currentCards => ({...currentCards, [listId]: [...(currentCards[listId] ?? []), newCard]}));
        persistStructure(listsRef.current, nextCards);
    };

    const requestAddCard = listId => setAddCardRequests(current => ({...current, [listId]: (current[listId] ?? 0) + 1}));
    const updateListTitle = (listId, title) => {
        const nextLists = commitLists(current => current.map(list => list.id === listId ? {...list, title, isNew: false} : list));
        persistStructure(nextLists);
    };
    const toggleCard = cardId => updateCard(cardId, card => ({completed: !card.completed}), "Alterou a situação do cartão");
    const createBoardLabel = label => {
        const nextLabels = commitLabels(current => [...current, label]);
        persistLabels(nextLabels);
    };
    const updateBoardLabel = (labelId, changes) => {
        const nextLabels = commitLabels(current => current.map(label => String(label.id) === String(labelId) ? {...label, ...changes} : label));
        commitCards(current => Object.fromEntries(Object.entries(current).map(([listId, listCards]) => [
            listId,
            listCards.map(card => ({
                ...card,
                labels: (card.labels ?? []).map(label => String(label.id) === String(labelId) ? {...label, ...changes} : label),
            })),
        ])));
        persistLabels(nextLabels);
    };
    const deleteBoardLabel = labelId => {
        const nextLabels = commitLabels(current => current.filter(label => String(label.id) !== String(labelId)));
        commitCards(current => Object.fromEntries(Object.entries(current).map(([listId, listCards]) => [
            listId,
            listCards.map(card => ({...card, labels: (card.labels ?? []).filter(label => String(label.id) !== String(labelId))})),
        ])));
        persistLabels(nextLabels);
    };

    const addList = () => {
        const id = uniqueId("list");
        commitLists(current => [...current, {id, clientId: id, title: "", pinned: false, isNew: true}]);
        commitCards(current => ({...current, [id]: []}));
    };

    const copyList = listId => {
        const source = listsRef.current.find(list => list.id === listId);
        if (!source) return;
        const id = uniqueId("list");
        const copiedCards = (cardsRef.current[listId] ?? []).map(card => {
            const cardId = uniqueId("demand");
            return {
                ...card,
                id: cardId,
                clientId: cardId,
                title: `${card.title} (cópia)`,
                attachments: [],
                history: (card.history ?? []).map(history => ({...history})),
            };
        });
        const nextLists = commitLists(current => {
            const sourceIndex = current.findIndex(list => list.id === listId);
            const insertionIndex = source.pinned ? current.filter(list => list.pinned).length : sourceIndex + 1;
            return [...current.slice(0, insertionIndex), {...source, id, clientId: id, title: `${source.title} (cópia)`, pinned: false}, ...current.slice(insertionIndex)];
        });
        const nextCards = commitCards(current => ({...current, [id]: copiedCards}));
        persistStructure(nextLists, nextCards, null, copiedCards.map(card => card.id));
    };

    const moveList = (listId, targetIndex) => {
        const nextLists = commitLists(current => {
            const from = current.findIndex(list => list.id === listId);
            if (from < 0) return current;
            const movingList = current[from];
            const pinnedCount = current.filter(list => list.pinned).length;
            const safeTarget = movingList.pinned ? Math.min(targetIndex, Math.max(0, pinnedCount - 1)) : Math.max(targetIndex, pinnedCount);
            return arrayMove(current, from, safeTarget);
        });
        persistStructure(nextLists);
    };

    const moveAllCards = (sourceId, targetId) => {
        const nextCards = commitCards(current => ({
            ...current,
            [sourceId]: [],
            [targetId]: [...(current[targetId] ?? []), ...(current[sourceId] ?? [])],
        }));
        persistStructure(listsRef.current, nextCards);
    };

    const togglePin = listId => {
        const nextLists = commitLists(current => {
            const index = current.findIndex(list => list.id === listId);
            if (index < 0) return current;
            const list = {...current[index], pinned: !current[index].pinned};
            const remaining = current.filter(item => item.id !== listId);
            const target = remaining.filter(item => item.pinned).length;
            return [...remaining.slice(0, target), list, ...remaining.slice(target)];
        });
        persistStructure(nextLists);
    };

    const deleteList = listId => {
        const list = lists.find(item => item.id === listId);
        const total = cards[listId]?.length ?? 0;
        MySwal.fire({
            icon: "warning",
            title: "Excluir lista?",
            text: `A lista “${list?.title}”${total ? ` e seus ${total} cartão(ões)` : ""} será excluída. Esta ação não poderá ser desfeita.`,
            showCancelButton: true,
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: "#e64942",
            reverseButtons: true,
        }).then(({isConfirmed}) => {
            if (!isConfirmed) return;
            const nextLists = commitLists(current => current.filter(item => item.id !== listId));
            const nextCards = commitCards(current => Object.fromEntries(Object.entries(current).filter(([id]) => id !== listId)));
            persistStructure(nextLists, nextCards);
        });
    };

    const discardList = listId => {
        commitLists(current => current.filter(item => item.id !== listId));
        commitCards(current => Object.fromEntries(Object.entries(current).filter(([id]) => id !== listId)));
    };

    const deleteCard = cardId => {
        const card = Object.values(cards).flat().find(item => item.id === cardId);
        MySwal.fire({
            icon: "warning",
            title: "Excluir cartão?",
            text: `O cartão “${card?.title}” será excluído. Esta ação não poderá ser desfeita.`,
            showCancelButton: true,
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: "#e64942",
            reverseButtons: true,
        }).then(({isConfirmed}) => {
            if (!isConfirmed) return;
            const nextCards = commitCards(current => Object.fromEntries(Object.entries(current).map(([listId, listCards]) => [listId, listCards.filter(item => item.id !== cardId)])));
            persistStructure(listsRef.current, nextCards);
            setSelectedCardId(null);
        });
    };

    return (
        <div className="flex h-full min-h-[560px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
            <BoardHeader board={board} onBack={onBack} isSaving={isSaving} isEditing={isEditing} editingUser={editingUser} onStartEditing={onStartEditing} onStopEditing={onStopEditing} isChangingEditing={isChangingEditing} />
            <DragDropProvider onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
                <main className="flex flex-1 items-start gap-4 overflow-x-auto overflow-y-hidden p-4">
                    {lists.map((list, listIndex) => <BoardList key={list.id} list={list} listIndex={listIndex} lists={lists} cards={cards[list.id] ?? []} users={users} onAddCard={addCard} onRequestAddCard={requestAddCard} onUpdateListTitle={updateListTitle} onToggleCard={toggleCard} onOpenCard={setSelectedCardId} onCopyList={copyList} onMoveList={moveList} onMoveAllCards={moveAllCards} onTogglePin={togglePin} onDeleteList={deleteList} onDiscardList={discardList} addCardRequest={addCardRequests[list.id] ?? 0} isDragDisabled={!isEditing} readOnly={!isEditing} />)}
                    {isEditing && <Button type="button" target="primary" onClick={addList} col="w-[200px] shrink-0 !border-dashed !py-3"><FaPlus /> Nova lista</Button>}
                </main>
            </DragDropProvider>

            {selectedCard && <CardDetailsModal card={selectedCard} listName={selectedCardList?.title} users={users} boardLabels={boardLabels} onCreateBoardLabel={createBoardLabel} onUpdateBoardLabel={updateBoardLabel} onDeleteBoardLabel={deleteBoardLabel} onCreateComment={text => createCardComment(selectedCard, text)} onUpdateComment={(commentId, text) => updateCardComment(selectedCard, commentId, text)} onDeleteComment={commentId => deleteCardComment(selectedCard, commentId)} onClose={() => setSelectedCardId(null)} onUpdate={(changes, historyText) => updateCard(selectedCard.id, changes, historyText)} onDelete={() => deleteCard(selectedCard.id)} isSaving={isSaving} readOnly={!isEditing} />}
        </div>
    );
};

export default DemandBoard;
